<template>
  <SkeletonLoader
    v-if="loading"
    width="100%"
    height="52px"
    class="h-[52px] relative mx-auto max-w-sm"
  />

  <div
    v-else
    class="voucher-container box-border min-h-12 w-full max-w-sm mx-auto break-all rounded outline-2 outline-dashed
      flex flex-col items-center px-4 py-2.5 transition-all duration-300 ease-in-out"
    :class="{
      'bg-EXPONDO-white outline-EXPONDO-secondary-medium': !copied && voucherIsValid,
      'bg-EXPONDO-white outline-EXPONDO-grey-60': voucherExists && !voucherIsValid,
      'bg-EXPONDO-red-light outline-EXPONDO-red': !voucherExists,
      'bg-EXPONDO-green-light outline-EXPONDO-green-medium': copied,
      'cursor-pointer': voucherIsValid,
    }"
    @click="copyVoucherCode"
  >
    <div
      v-if="voucherExists"
      class="flex items-center justify-center relative font-bold transition-all duration-300 ease-in-out text-center"
    >
      <div
        class="flex flex-col gap-2.5"
        :class="copied ? 'opacity-0' : 'opacity-100'"
      >
        <span class="text-2xl text-EXPONDO-primary">{{ voucherCode }}</span>

        <span
          v-if="!voucherIsValid"
          class="text-sm font-normal text-EXPONDO-red"
        >
          {{ $t('Code already used or expired') }}
        </span>
      </div>

      <div
        class="absolute m-auto flex items-center gap-2 text-EXPONDO-green pointer-events-none"
        :class="copied ? 'opacity-100' : 'opacity-0'"
      >
        <span class="text-base uppercase text-green-700">{{ $t('Voucher copied') }}</span>
        <span class="iconify fa-pro-solid--copy" />
      </div>
    </div>

    <div
      v-else
      class="text-2xl font-bold transition-all duration-300 ease-in-out text-EXPONDO-red text-center"
    >
      {{ $t('Invalid voucher code') }}
    </div>
  </div>
</template>

<script lang="ts">

import { Logger } from '~~/helpers/logger';

import type { VoucherStatus } from '~~/types/vouchers/voucher-status';
import { VOUCHER_STATUS } from '~~/types/vouchers/voucher-status';

export default defineComponent({
  name: 'VoucherCode',
  props: {
    voucherCode: {
      type: String,
      default: '',
    },
    voucherValidUntil: {
      type: String,
      default: null,
    },
  },
  emits: ['voucher-status'],
  setup(props, { emit }) {

    const copied = ref<boolean>(false);

    const {
      loading,
      voucherExists,
      voucherIsValid,
      search,
    } = useVoucherValidation(props.voucherCode, props.voucherValidUntil);

    const voucherStatus = computed<VoucherStatus>(() => {

      if (!voucherExists.value) {

        return VOUCHER_STATUS.invalid;

      }

      if (!voucherIsValid.value) {

        return VOUCHER_STATUS.expired;

      }

      return VOUCHER_STATUS.valid;
    
    });

    watch(voucherStatus, (newStatus) => {

      emit('voucher-status', newStatus);
    
    });

    const copyVoucherCode = async () => {

      if (!voucherIsValid.value) {

        return;

      }
      try {

        await navigator.clipboard.writeText(props.voucherCode);
        copied.value = true;
        setTimeout(() => {

          copied.value = false;
        
        }, 2000);
      
      } catch (err) {

        Logger.error('Error copying voucher code to clipboard: ', err);
      
      }
    
    };

    onMounted(async () => {

      await search();
      emit('voucher-status', voucherStatus.value);
    
    });

    return {
      loading,
      copied,
      copyVoucherCode,
      voucherExists,
      voucherIsValid,
    };
  
  },
});

</script>
